import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState(() => localStorage.getItem("qwiri_admin_key") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/demo-requests`, { headers: { "x-admin-key": key } });
      localStorage.setItem("qwiri_admin_key", key);
      setData(res.data);
    } catch (err) {
      setData(null);
      setError(err.response?.status === 401 ? "Invalid admin key." : "Could not load demo requests.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-black" data-testid="admin-page">
      <header className="border-b-2 border-black">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 py-4 md:px-8">
          <Link to="/" data-testid="admin-back-link" className="text-xl font-extrabold tracking-tighter">
            Qwiri.ai
          </Link>
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/50">Demo requests</span>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 py-14 md:px-8">
        <h1 className="text-[28px] font-bold tracking-tight md:text-[40px]">Demo requests</h1>
        <p className="mt-2 max-w-lg text-sm text-black/60 md:text-base">
          Every Book-a-demo submission from the landing page, newest first.
        </p>

        <form onSubmit={load} className="mt-8 flex max-w-xl flex-col gap-3 sm:flex-row" data-testid="admin-key-form">
          <input
            data-testid="admin-key-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            className="flex-1 border-2 border-black bg-white px-4 py-3 text-sm outline-none placeholder-black/40 focus:bg-black focus:text-white"
          />
          <button
            data-testid="admin-load-btn"
            type="submit"
            disabled={loading || !key}
            className="border-2 border-black bg-black px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-white hover:text-black disabled:opacity-50"
          >
            {loading ? "Loading…" : "Load requests"}
          </button>
        </form>

        {error && (
          <div data-testid="admin-error" className="mt-6 w-fit border-2 border-black bg-black px-5 py-3 text-sm font-semibold text-white">
            {error}
          </div>
        )}

        {data && (
          <div className="mt-10" data-testid="admin-results">
            <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.2em] text-black/50" data-testid="admin-count">
              {data.count} request{data.count === 1 ? "" : "s"}
            </div>
            {data.requests.length === 0 ? (
              <div className="border-2 border-black p-10 text-sm text-black/60" data-testid="admin-empty">
                No demo requests yet. Submit one from the landing page to see it here.
              </div>
            ) : (
              <div className="overflow-x-auto border-2 border-black">
                <table className="w-full min-w-[820px] text-left text-sm" data-testid="admin-table">
                  <thead>
                    <tr className="border-b-2 border-black bg-black text-white">
                      {["Received", "Name", "Email", "Company", "Size", "Challenge"].map((h) => (
                        <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/15">
                    {data.requests.map((r) => (
                      <tr key={r.id} className="align-top transition-colors duration-100 hover:bg-black hover:text-white" data-testid={`admin-row-${r.id}`}>
                        <td className="whitespace-nowrap px-4 py-3 text-xs opacity-70">
                          {new Date(r.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 font-bold">{r.name}</td>
                        <td className="px-4 py-3">{r.email}</td>
                        <td className="px-4 py-3">{r.company}</td>
                        <td className="whitespace-nowrap px-4 py-3">{r.company_size}</td>
                        <td className="max-w-[280px] px-4 py-3 text-xs opacity-80">{r.message || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
